<script lang="js">
	export default {
		name: 'BaseTypeAhead',
		props: {
			// the suggestion list, it can be an array of any type but if no template is provided for the item display an object is expected
			list: { type: Array, required: false, default: () => [] },
			// field name to display in the item list and value returned in the input event, must be a property name of each list item
			field: { type: String, required: true },
			// custom class for the list wrapper
			suggestionListClass: { type: String, required: false, default: '' },
			// custom class for the item wrapper
			suggestionItemClass: { type: String, required: false, default: '' },
			// custom class for the focused state of an item (apply on the wrapper)
			suggestionItemFocusClass: { type: String, required: false, default: 'suggestion__item--focus' },
			// custom class for the no result message wrapper
			noResultClass: { type: String, required: false, default: '' },
			// either or not a no result message should be displayed
			displayNoResult: { type: Boolean, required: false, default: true },
			// input value
			value: { type: String, required: true },
			// should the suggestions being displayed on input focus (act like a searchable select)
			displayOnFocus: { type: Boolean, required: false, default: false },
			// don't display the suggestion list
			disableSuggestion: { type: Boolean, required: false, default: false },
			// show loading spinner
			isLoading: { type: Boolean, required: false, default: false },
		},
		data() {
			return {
				focused: {
					input: false,
					suggestion: false
				},
				forceHide: false,
				focusedSuggestionItem: -1,
				uuid: this.generateUUID()
			};
		},
		computed: {
			/**
			 * control the display of the list
			 * only displayed if:
			 *  - the the input or the list itself are in focus,
			 *  - and the input is not empty and either the list is not empty or the displayNoResult is set to true
			 *  - or the displayOnFocus flag is true
			 *  - the forceHide flag is set to false
			 *  - the disableSuggestion flag is set to false
			 */
			showList() {
				return (this.focused.input || this.focused.suggestion)
					&& (
						(this.value.length > 0 && (this.list.length > 0 || this.displayNoResult))
						|| this.displayOnFocus
					)
					&& !this.forceHide
					&& !this.disableSuggestion;
			}
		},
		watch: {
			value() {
				this.$emit('input', this.value);
				this.forceHide = false;
				this.focusedSuggestionItem = -1;
			},
			// emit an event when the display status of the list change
			showList(newVal, oldVal) {
				// reset the focused item index
				this.focusedSuggestionItem = -1;

				if (newVal !== oldVal) {
					this.$emit('typeahead-display-list', newVal);
				}
			},
			focusedSuggestionItem() {
				// scroll the list to keep the highlighted element into view
				const elList = this.$refs.suggestionList;

				elList.scrollTo({ behavior: 'smooth', top: (elList.scrollHeight / elList.childElementCount) * this.focusedSuggestionItem - elList.clientHeight / 2 });
			}
		},
		mounted() {
			document.addEventListener('click', this.onDocumentClick);
		},
		beforeDestroy() {
			document.removeEventListener('click', this.onDocumentClick);
		},
		methods: {
			/**
			 * run on input focus
			 * toggle the saved focus state of the input
			 */
			onFocusin() {
				this.focused.input = true;
			},
			/**
			 * run on input blur
			 * reset all display condition
			 */
			onFocusout() {
				this.focused.input = false;
				this.forceHide = false;
			},
			/**
			 * run on item clic
			 * emit an input event with the selected value
			 * emit a select-item event with the complete item object selected
			 * @param {Object} item selected item
			 */
			onSelect(item) {
				this.focused.suggestion = false;
				this.$emit('input', item[this.field]);
				this.$emit('select-item', item);
			},
			onDocumentClick(event) {
				const isOnComponent = typeof event.composedPath().find(el => el.id === this.uuid) !== 'undefined';

				// if the click is outside the component close the list
				if (!isOnComponent) {
					this.focused.suggestion = false;
				}

			},
			/**
			 * emulate the keyboard navigation in the suggestion list (highlight the previous item if exist)
			 */
			onArrowUp(event) {
				event.preventDefault();
				if (this.focusedSuggestionItem > 0) {
					this.focusedSuggestionItem -= 1;
				}
				else {
					this.focusedSuggestionItem = this.list.length - 1;
				}
			},
			/**
			 * emulate the keyboard navigation in the suggestion list (highlight the next item if exist)
			 */
			onArrowDown(event) {
				event.preventDefault();
				if (this.focusedSuggestionItem < this.list.length - 1) {
					this.focusedSuggestionItem += 1;
				}
				else {
					this.focusedSuggestionItem = 0;
				}
			},
			/**
			 * emulate the keyboard navigation in the suggestion list (select highlighted item)
			 */
			onEnter(event) {
				if (this.focusedSuggestionItem >= 0 && this.showList) {
					event.stopPropagation();
					event.preventDefault();
					// blur the input to prevent an issue with basestylabletextarea not updating if the editor is focused and the value change
					document.activeElement.blur();
					this.onSelect(this.list[this.focusedSuggestionItem]);
				}
			}
		}
	};
</script>

<template lang="html">
  <div
    :id="uuid"
    class="typeahead"
    @keydown.up="onArrowUp"
    @keydown.down="onArrowDown"
    @keydown.enter.capture="onEnter"
  >
    <!-- input wrapper -->
    <div
      class="typeahead_input"
      @focusin="onFocusin"
      @focusout="onFocusout"
      @keyup.esc="forceHide = true"
    >
      <slot name="input">
        <p>
          input goes here, is you see this text add &lt;template v-slot:input&gt; &lt;/template&gt; with your
          input inside
        </p>
      </slot>
    </div>

    <!-- list and no result message wrapper -->
    <div
      v-show="showList"
      ref="suggestionList"
      class="typeahead__suggestion"
      :class="[suggestionListClass, focusedSuggestionItem !== -1 ? 'typeahead__suggestion--focuswithin' : '']"
      @mousedown="focused.suggestion = true"
    >
      <div
	  	v-show="isLoading"
	  >
	  	<b-spinner
			small
			label="Small Spinner"
			class="mt-2 ml-3"
		/>
	  </div>
	  <!-- suggestion item wrapper -->
      <div
        v-for="(item, index) in list"
        :key="`${item.id}_${item.type}_${Math.floor(Math.random() * 1001 + 1)}`"
        class="suggestion__item"
        :class="[suggestionItemClass, index === focusedSuggestionItem ? suggestionItemFocusClass : '']"
        @mouseup.left="onSelect(item)"
        @focus="focusedSuggestionItem = index"
      >
        <slot
          name="suggestion"
          :item="item"
        >
          <!-- fallback if not custom suggestion template is provided -->
          {{ item[field] }}
        </slot>
      </div>

      <!-- no result message wrapper -->
      <div
        v-if="list.length == 0 && displayNoResult"
        :class="noResultClass"
      >
        <slot name="noresult">
          <!-- fallback if not template is provided -->
          Aucun résultat trouvé
        </slot>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
	.typeahead {
		&__suggestion {
			margin-top: 2px;
			background-color: #fff;
			box-shadow: 0 1px 6px rgba(204, 204, 204, 0.51);
			padding: 0.25em 0;
			position: absolute;
			z-index: 50;
			width: 100%;
			max-height: 300px;
			overflow-y: auto;
			scrollbar-color: transparent transparent;
			scrollbar-width: thin;
			@media screen and (prefers-reduced-motion: no-preference) {
				transition: scrollbar-color 300ms ease-in-out;
			}
			cursor: pointer;
			overscroll-behavior: contain;

			&:hover,
			&--focuswithin {
				scrollbar-color: #aeaeae transparent;
			}
		}
		.suggestion__item--focus {
			background-color: #0002;
		}
	}
</style>
